import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { PanelAction } from '@datachain/ui-sdk/components/app-panel';
import { AppIcons } from '@datachain/ui-sdk/components/dc-icon';
import { Map, OrderedMap } from 'immutable';
import {
	BehaviorSubject,
	filter,
	firstValueFrom,
	map,
	Observable,
	takeUntil,
	tap,
} from 'rxjs';

import { ActionIcons } from '../../../ui/app-actions.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { ActionIconsInjectable } from '../../../ui/ui.module';
import {
	ColumnConfigErrorKey,
	ExpositionColumnConfigListEntity,
} from '../../domain/exposition-column-config-list.entity';
import { ExpositionColumnConfigEntity } from '../../domain/exposition-column-config.entity';
import { MoveColumnItem } from '../../domain/exposition.types';
import { ExpositionColumnsConfigComponent } from '../exposition-columns-config/exposition-columns-config.component';

type ColumnsConfigMode = 'COLUMNS_PARAMETER' | 'COLUMNS_ORDER';

enum ColumnConfigAction {
	CaseInsensitive = 'case-insensitive',
	Filter = 'filter',
	Hash = 'hash',
	Hide = 'hide',
}

@Component({
	selector: 'app-exposition-columns-config-extended',
	templateUrl: './exposition-columns-config-extended.component.html',
	styleUrls: ['./exposition-columns-config-extended.component.scss'],
	inputs: ['columnConfigList'],
})
export class ExpositionColumnsConfigExtendedComponent
	extends DcBaseComponent
	implements OnInit
{
	protected readonly ColumnConfigErrorKey = ColumnConfigErrorKey;
	protected readonly DcIcons = DcIcons;

	@ViewChild('columnsConfigGrid')
	public columnsConfigCmp: ExpositionColumnsConfigComponent | null = null;

	public vo$: Observable<{
		columnsConfiguration: ExpositionColumnConfigListEntity;
		columnsOrder: ExpositionColumnConfigListEntity;
		columnConfigActions: Array<PanelAction>;
		columnsConfigMode: ColumnsConfigMode;
	}>;

	private readonly currentConfigMode$ = new BehaviorSubject<ColumnsConfigMode>(
		'COLUMNS_ORDER'
	);

	private readonly columnConfigList$ =
		new BehaviorSubject<ExpositionColumnConfigListEntity>(
			ExpositionColumnConfigListEntity.build([])
		);

	private readonly columnConfigActions$ = new BehaviorSubject<
		Array<PanelAction>
	>([]);

	private readonly columnsConfigActions: Array<PanelAction> = [
		{
			order: 1,
			command: (): Promise<void> =>
				this.triggerAction(ColumnConfigAction.Filter),
			iconName: '',
			description: $localize`:@@expositions.columns.config.action.filter:`,
			typed: 'grouped-context-action',
		},
		{
			order: 2,
			command: (): Promise<void> =>
				this.triggerAction(ColumnConfigAction.CaseInsensitive),
			iconName: '',
			description: $localize`:@@expositions.columns.config.action.caseInsensitive:`,
			typed: 'grouped-context-action',
		},
		{
			order: 3,
			command: (): Promise<void> => this.triggerAction(ColumnConfigAction.Hash),
			iconName: '',
			description: $localize`:@@expositions.columns.config.action.hashable:`,
			typed: 'grouped-context-action',
		},
		{
			order: 4,
			command: (): Promise<void> => this.triggerAction(ColumnConfigAction.Hide),
			iconName: '',
			description: $localize`:@@expositions.columns.config.action.hide:`,
			typed: 'grouped-context-action',
		},
	];

	public constructor(
		@Inject(ActionIconsInjectable) private readonly actionIcons: AppIcons
	) {
		super();
		this.cmpId = 'exposition-columns-config-extended';

		this.vo$ = combineAll({
			columnsConfiguration: this.columnConfigList$.pipe(
				tap((l) => this.bootstrapIcons(l))
			),
			columnsOrder: this.columnConfigList$.pipe(
				map((columnList) => {
					const updatedEls = OrderedMap(
						columnList.elements
							.filter((els) => !els.isHidden)
							.entrySeq()
							.sort(([, colA], [, colB]) => colA.position - colB.position)
					);

					return columnList.cloneWithElements(updatedEls);
				})
			),
			columnConfigActions: this.columnConfigActions$,
			columnsConfigMode: this.currentConfigMode$,
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.handleSubscription();
	}

	public modifyColumnConfigValidity(): void {
		if (!this.columnsConfigCmp) {
			return;
		}
		const updatedList = this.columnsConfigCmp.getUpdatedList();
		this.columnConfigList$.next(updatedList);
	}

	public getUpdatedList(): ExpositionColumnConfigListEntity {
		return this.columnConfigList$.value;
	}

	public async moveAction(event: MoveColumnItem): Promise<void> {
		const columnConfig = await firstValueFrom(this.columnConfigList$);

		const { elemKey, direction } = event;

		const current = columnConfig.elements.get(elemKey);
		if (!current) {
			return;
		}
		let updatedElements = columnConfig.elements;

		if (direction === 'top') {
			columnConfig.elements
				.filter((el) => !el.isHidden)
				.forEach((element) => {
					if (element.position < current.position) {
						updatedElements = updatedElements.set(
							element.id,
							element.updatePosition(element.position + 1)
						);
					}
				});

			updatedElements = updatedElements.set(elemKey, current.updatePosition(1));
			this.columnConfigList$.next(columnConfig.setElements(updatedElements));
		} else if (direction === 'bottom') {
			const visibleEls = columnConfig.elements.filter((el) => !el.isHidden);

			visibleEls.forEach((element) => {
				if (element.position > current.position) {
					updatedElements = updatedElements.set(
						element.id,
						element.updatePosition(element.position - 1)
					);
				}
			});

			updatedElements = updatedElements.set(
				elemKey,
				current.updatePosition(visibleEls.size)
			);
		} else {
			const targetPosition =
				direction === 'up' ? current.position - 1 : current.position + 1;

			const target = columnConfig.elements.find(
				(c) => c.position === targetPosition
			);

			if (!target) {
				return;
			}

			updatedElements = columnConfig.elements
				.set(event.elemKey, current.updatePosition(targetPosition))
				.set(target.id, target.updatePosition(current.position));
		}
		this.columnConfigList$.next(columnConfig.setElements(updatedElements));
	}

	public setColumnsConfigMode(mode: ColumnsConfigMode): void {
		this.currentConfigMode$.next(mode);
	}

	public nextColumnsConfigMode(): void {
		this.currentConfigMode$.next('COLUMNS_ORDER');
	}

	public previousColumnsConfigMode(): void {
		this.currentConfigMode$.next('COLUMNS_PARAMETER');
	}

	private handleSubscription(): void {
		this.toObservable<ExpositionColumnConfigListEntity>('columnConfigList')
			.pipe(
				takeUntil(this.onDestroy$),
				filter((list) => list.elements.size !== 0),
				tap((columnList) => {
					const lastPosition =
						columnList.elements
							.valueSeq()
							.map((item) => item.position)
							.max() ?? 0;

					let updatedElements: Map<string, ExpositionColumnConfigEntity>;
					let position = 1;

					if (lastPosition === 0) {
						updatedElements = columnList.elements.map((el) => {
							if (el.isHidden) {
								return el.updatePosition(-1);
							}
							return el.updatePosition(position++);
						});
					} else {
						const sortedElements = columnList.elements
							.toList()
							.toArray()
							.sort((a, b) => {
								if (a.position === 0 && b.position !== 0) {
									return 1;
								}
								if (b.position === 0 && a.position !== 0) {
									return -1;
								}
								return a.position - b.position;
							});

						updatedElements = sortedElements.reduce<
							Map<string, ExpositionColumnConfigEntity>
						>((acc, curr) => {
							if (curr.position !== -1) {
								const item = curr.updatePosition(position);
								position++;
								return acc.set(curr.id, item);
							}
							return acc.set(curr.id, curr);
						}, Map());
					}

					this.columnConfigList$.next(columnList.setElements(updatedElements));
				})
			)
			.subscribe();
	}

	private async bootstrapIcons(
		list: ExpositionColumnConfigListEntity
	): Promise<void> {
		const actions = [ActionIcons.FilterWarning, ActionIcons.Filter];
		const actionMap = await firstValueFrom(
			this.actionIcons.getFromRegistry(...actions)
		);

		if (list.hasDuplicatedAliases || list.hasInvalid) {
			this.columnConfigActions$.next([
				...this.columnsConfigActions,
				{
					order: 0,
					command: (): void => {
						if (this.columnsConfigCmp) {
							this.columnsConfigCmp.filterErrors();
						}
					},
					iconName: actionMap.get(ActionIcons.FilterWarning)?.iconName,
					tooltip: $localize`:@@expositions.columns.config.action.filterDuplicates:`,
					description: $localize`:@@expositions.columns.config.action.filterDuplicates:`,
					typed: '',
				},
			]);
		} else {
			this.columnConfigActions$.next(this.columnsConfigActions);
		}
	}

	private async triggerAction(action: ColumnConfigAction): Promise<void> {
		if (!this.columnsConfigCmp) {
			return;
		}

		const selection =
			(await this.columnsConfigCmp.getSelectedColumns()) as Array<ExpositionColumnConfigEntity>;
		const columnsConfig = await firstValueFrom(this.columnConfigList$);

		const selectedIds = selection.map((s) => s.id);
		switch (action) {
			case ColumnConfigAction.CaseInsensitive:
				this.columnConfigList$.next(
					columnsConfig.updateIsCaseSensitive(selectedIds, true)
				);
				break;
			case ColumnConfigAction.Filter:
				this.columnConfigList$.next(
					columnsConfig.updateIsFiltered(selectedIds, true)
				);
				break;
			case ColumnConfigAction.Hash:
				this.columnConfigList$.next(
					columnsConfig.updateHash(selectedIds, true)
				);
				break;
			case ColumnConfigAction.Hide:
				this.columnConfigList$.next(
					columnsConfig.updateIsHidden(selectedIds, true)
				);
				break;
		}
	}
}
